"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const BUSINESS_NAME = "Briviox";
const TAGLINE = "Where Ambition Meets Achievement.";
const ADDRESS = "123 Education Lane, Innovation City, Countryland";
const PHONE = "+1 (555) 987-6543";
const EMAIL = "contact@briviox.com";
const SERVICES = ["Visa Assistance", "University Admissions Support", "Scholarship Guidance"];
const SOCIAL_LINKS = [{
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}];
const LEGAL_LINKS = [{
  label: "Privacy Policy",
  href: "#"
}, {
  label: "Terms of Service",
  href: "#"
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-primary-foreground/10 animate-pulse rounded" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li data-index={0}>
          <Link to="#" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
            UK
          </Link>
        </li>
        <li data-index={1}>
          <Link to="#" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
            Canada
          </Link>
        </li>
        <li data-index={2}>
          <Link to="#" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
            USA
          </Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="footer" className="bg-primary text-primary-foreground pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="flex flex-col gap-5">
            <WvcLogo className="h-8 w-auto brightness-0 invert" />
            <p className="text-sm text-primary-foreground/70 leading-relaxed max-w-xs">
              {TAGLINE}
            </p>
          </div>

          {}
          <div>
            <h3 className="font-default font-semibold text-lg mb-5">Destinations</h3>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="font-default font-semibold text-lg mb-5">Services</h3>
            <ul className="space-y-3">
              {SERVICES.map((service, i) => <li key={i} data-index={i}>
                  <Link to="#" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                    {service}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div>
            <h3 className="font-default font-semibold text-lg mb-5">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-primary-foreground/70">
                <MapPin className="w-5 h-5 shrink-0 text-primary-foreground/50 mt-0.5" />
                <span>{ADDRESS}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-primary-foreground/70">
                <Phone className="w-5 h-5 shrink-0 text-primary-foreground/50" />
                <span>{PHONE}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-primary-foreground/70">
                <Mail className="w-5 h-5 shrink-0 text-primary-foreground/50" />
                <span>{EMAIL}</span>
              </li>
            </ul>
            
            <div className="flex items-center gap-4 mt-8">
              {SOCIAL_LINKS.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} to={social.href} data-index={i} className="w-8 h-8 rounded-full bg-primary-foreground/10 flex items-center justify-center text-primary-foreground hover:bg-primary-foreground hover:text-primary transition-colors" aria-label={social.label}>
                    <Icon className="w-4 h-4" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        {}
        <Separator className="bg-primary-foreground/10 mb-6" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-primary-foreground/70">
          <p>
            &copy; {currentYear} {BUSINESS_NAME}. All rights reserved.
          </p>
          
          <div className="flex items-center gap-3">
            {LEGAL_LINKS.map((link, i) => <React.Fragment key={i}>
                <div data-index={i}>
                  <Link to={link.href} className="hover:text-primary-foreground transition-colors">
                    {link.label}
                  </Link>
                </div>
                {i < LEGAL_LINKS.length - 1 && <Separator orientation="vertical" className="h-3.5 bg-primary-foreground/20" />}
              </React.Fragment>)}
          </div>
        </div>
      </div>
    </section>;
}